import { useState } from 'react'

import type { ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  DemandGroupDirectoryEntry,
  GetDemandGroupDirectoryResponse,
} from '@ebalance/proto/bffuser/v1/demand_group_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Alert,
  Box,
  Flex,
  LightModeSemanticColors,
  Link,
  Spacing,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaFileLinesRegularFull } from '@enechain/ecloud-icons'

import {
  EMPTY_PANEL_ICON_SIZE,
  EmptyPanel,
} from '~/components/common/EmptyPanel'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { RegionLoading } from '~/components/common/RegionLoading'
import { convertAreaLabel } from '~/models/area'
import { useDemandGroupMembers } from '~/pages/DemandSettings/hooks/useDemandGroupMembers'
import {
  entriesInArea,
  retailerLabel,
} from '~/pages/DemandSettings/models/entriesInArea'
import { routePaths } from '~/routes/routePaths'

type Member = { supplyPointSpecificNumber: string; name?: string }

/** 選択行の強調 (design `.dg-list-table tbody tr.active td`)。 */
const SELECTED_ROW_STYLE: React.CSSProperties = {
  background: LightModeSemanticColors.Surface.accentPrimaryLightLow.value,
}

/**
 * 需要想定設定画面の「需要グループ」タブ。選んだエリアの需要グループ一覧と、選んだグループの
 * 所属供給地点を見る。追加・編集・削除は CSV 入出力画面で行う (導線は画面側の toolbar 右端)。
 * エリアの切り替えと目録の取得・公開判定は画面側 (全タブ共通)。
 *
 * 受け取る area が変わると選択は外れる (AC-CDG-002)。
 */
export const DemandGroupsTab = ({
  area,
  directory,
  isAreaLoading,
}: {
  area: AreaCode | undefined
  directory: UseQueryResult<GetDemandGroupDirectoryResponse, ConnectError>
  isAreaLoading: boolean
}): React.ReactElement => {
  const { t } = useTranslation()
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const [selectedArea, setSelectedArea] = useState(area)
  // 受け取る area が変わったら選択を外す (AC-CDG-002。前のエリアへ戻っても復活させない)。
  // effect ではなく描画中の state 調整で行い、古い選択で所属を 1 描画だけ取りに行かないようにする。
  if (selectedArea !== area) {
    setSelectedArea(area)
    setSelectedId(undefined)
  }
  const rows = entriesInArea(directory.data?.entries ?? [], area)
  // 一覧に無い id (再取得で消えた) も未選択として扱う。
  const selected = rows.find((row) => row.demandGroupId === selectedId)
  const members = useDemandGroupMembers(selected?.demandGroupId)
  const isLoading = directory.isLoading || isAreaLoading

  const groupColumns: Array<MasterDataTableColumn<DemandGroupDirectoryEntry>> =
    [
      {
        key: 'groupName',
        header: t('page:adminDemandGroups.list.columns.groupName'),
        cell: (row) => row.groupName,
      },
      {
        key: 'retailer',
        header: t('page:adminDemandGroups.list.columns.retailer'),
        cell: (row) => retailerLabel(row),
      },
      {
        key: 'memberCount',
        header: t('page:adminDemandGroups.list.columns.memberCount'),
        format: 'number',
        cell: (row) => row.activeMemberCount.toLocaleString(),
      },
    ]

  const memberColumns: Array<MasterDataTableColumn<Member>> = [
    {
      key: 'supplyPointSpecificNumber',
      header: t('page:adminDemandGroups.detail.columns.supplyPoint'),
      nowrap: true,
      cell: renderSupplyPointNumber,
    },
    {
      key: 'name',
      header: t('page:adminDemandGroups.detail.columns.name'),
      cell: (row) => row.name ?? '',
    },
  ]

  return (
    <VStack alignItems="stretch" flex="1" gap={4} minHeight="0">
      {directory.isError && (
        <Alert status="error" title={t('page:adminDemandGroups.loadError')} />
      )}
      <Text
        as="p"
        color="Text.highEmphasis"
        fontSize="md"
        style={{ margin: 0, lineHeight: 1.6 }}
      >
        {t('page:adminDemandGroups.description.prefix')}
        <Link asChild>
          <RouterLink to={routePaths.balanceDemand.path}>
            {t('page:adminDemandGroups.description.linkLabel')}
          </RouterLink>
        </Link>
        {t('page:adminDemandGroups.description.suffix')}
      </Text>

      {!directory.isError && (
        <RegionLoading isLoading={isLoading}>
          <Flex
            flexWrap="wrap"
            flex="1"
            overflow="auto"
            minHeight="0"
            border="1px solid"
            borderColor="Border.lowEmphasis"
            borderRadius="md"
          >
            <Box
              flex="0 0 376px"
              minWidth="0"
              borderColor="Border.lowEmphasis"
              borderRight="1px solid"
            >
              <Text
                as="h2"
                textStyle="sectionTitle"
                style={{ margin: 0, padding: '12px 16px' }}
              >
                {t('page:adminDemandGroups.list.title')}
                {area !== undefined && ` (${convertAreaLabel(area)})`}
              </Text>
              <MasterDataTable
                columns={groupColumns}
                emptyLabel={t('page:adminDemandGroups.list.empty')}
                getRowKey={(row) => row.demandGroupId}
                getRowStyle={(row) =>
                  row.demandGroupId === selected?.demandGroupId
                    ? SELECTED_ROW_STYLE
                    : undefined
                }
                isLoading={isLoading}
                onRowClick={(row) => setSelectedId(row.demandGroupId)}
                rows={rows}
              />
            </Box>

            <Box flex="1 1 0" minWidth="0">
              <Text
                as="h2"
                textStyle="sectionTitle"
                style={{ margin: 0, padding: '12px 16px' }}
              >
                {t('page:adminDemandGroups.detail.title')}
              </Text>
              {selected === undefined ? (
                <EmptyPanel
                  description={t('page:adminDemandGroups.detail.emptyDesc')}
                  icon={
                    <FaFileLinesRegularFull
                      boxSize={EMPTY_PANEL_ICON_SIZE}
                      aria-hidden="true"
                    />
                  }
                  title={t('page:adminDemandGroups.detail.emptyTitle')}
                />
              ) : (
                <Flex
                  flexDirection="column"
                  gap={Spacing[3].value}
                  padding={Spacing[4].value}
                >
                  <DetailFieldRow
                    label={t('page:adminDemandGroups.detail.groupNameLabel')}
                    value={selected.groupName}
                  />
                  <DetailFieldRow
                    label={t('page:adminDemandGroups.detail.retailerLabel')}
                    value={retailerLabel(selected)}
                  />
                  {members.isError ? (
                    <Alert
                      status="error"
                      title={t('page:adminDemandGroups.detail.loadError')}
                    />
                  ) : (
                    <MasterDataTable
                      columns={memberColumns}
                      emptyLabel={t('page:adminDemandGroups.detail.noMembers')}
                      getRowKey={(row) => row.supplyPointSpecificNumber}
                      isLoading={members.isLoading}
                      rows={members.data?.members ?? []}
                    />
                  )}
                </Flex>
              )}
            </Box>
          </Flex>
        </RegionLoading>
      )}
    </VStack>
  )
}

/** 22 桁の番号で表を横スクロールさせないよう sm・等幅数字にする (design `.dg-sp-mono`)。 */
const renderSupplyPointNumber = (row: Member): React.ReactElement => (
  <Text as="span" textStyle="sm" fontVariantNumeric="tabular-nums">
    {row.supplyPointSpecificNumber}
  </Text>
)

/** 右ペインの属性欄 1 行 (design `.dg-field-row`: ラベル列 84px・ラベル左・値右)。 */
const DetailFieldRow = ({
  label,
  value,
}: {
  label: string
  value: string
}): React.ReactElement => (
  <Flex alignItems="flex-start" gap={Spacing[2].value}>
    <Text flex="0 0 84px" color="Text.highEmphasis" fontWeight="bold">
      {label}
    </Text>
    <Text>{value}</Text>
  </Flex>
)
